<template>
  <div v-if="isShowSkeletonRows" :class="$style.catalogueSkeletonWrapper">
    <CatalogueRowSkeleton :class="$style.catalogueSkeletonItem" />
    <CatalogueRowSkeleton :class="$style.catalogueSkeletonItem" />
    <CatalogueRowSkeleton :class="$style.catalogueSkeletonItem" />
  </div>
  <div v-else ref="gamesCatalogueContentRef" :class="$style.gamesCatalogueContainer">
    <template v-if="isLoading && gamesCount">
      <GameItemSkeleton v-for="index in gamesCount" :key="index" />
    </template>
    <template v-else>
      <GameThumbnail
        v-for="game of gamesList"
        :key="game.id"
        ref="gamesCatalogueRef"
        :game="game"
        :gtm-action="gtmAction"
        :collection-id="storeCataloguePage.selectedCollection"
        :favorite-games="favoriteGames"
        @add-to-favorite="addToFavorite"
        @remove-from-favorite="removeFromFavorite"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { type ComponentPublicInstance, onMounted, ref } from 'vue';
import { GameThumbnail } from '~/components/lib/mainPage';
import GameItemSkeleton from '~/components/lib/skeleton/gameItemSkeleton.vue';
import CatalogueRowSkeleton from '~/components/mainPage/catalogueRowSkeleton.vue';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreInfoPages } from '~/stores/strapi/infoPages';
import { catalogueThumbsCount } from '~/utils/defaultData/catalogueThumbsCount';
import type { ISsGame } from '~/types/softswiss/ssGames';

const props = defineProps({
  gamesList: {
    type: Array as PropType<ISsGame[] | null>,
    default: null,
  },
  favoriteGames: {
    type: Array as PropType<number[]>,
    required: true,
  },
  gtmAction: {
    type: String,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: () => false,
  },
});
const emit = defineEmits<{
  (e: 'addToFavorite', value: number): void;
  (e: 'removeFromFavorite', value: number): void;
  (e: 'setCountCatalogueItem', value: number): void;
}>();

const storeInfoPages = useStoreInfoPages();
const storeCataloguePage = useStoreCataloguePage();

const gamesCatalogueContentRef = ref<HTMLElement | null>(null);
const gamesCatalogueRef = ref<ComponentPublicInstance[] | null>(null);

const gamesCount = ref(0);

const addToFavorite = (val: number) => {
  emit('addToFavorite', val);
};

const removeFromFavorite = (val: number) => {
  emit('removeFromFavorite', val);
};

const calculateCount = () => {
  const itemCount = catalogueThumbsCount(useStoreRoot().screen.width);
  const rowCount = 6;

  return itemCount * rowCount;
};
const isShowSkeletonRows = computed(() => {
  return props.isLoading && !gamesCount.value && !storeInfoPages.infoPages.length;
});

onMounted(() => {
  gamesCount.value = calculateCount();
  if (gamesCount.value >= 0) {
    emit('setCountCatalogueItem', gamesCount.value);
  }
});
</script>

<style lang="scss" module>
.catalogueSkeletonWrapper {
  display: flex !important;
  .catalogueSkeletonItem {
    @include mediaBreakpointPixelDown(481px) {
      gap: 16px;
    }
  }
}
</style>
